import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	useMemo,
} from 'react';
import _ from 'underscore';
import ResizeObserver from 'resize-observer-polyfill';

import {useUserPref} from '@netx/core-components/hooks/useUserPref';
import {
	Axis,
	DragResizeDirection,
	DRAG_RESIZE_DEFAULT_DIRECTION,
	ClientPosition,
	ClientDimension,
	OffsetDimension,
	ScrollDimension,
} from './constants';

const getAxisFromDirection = (direction: DragResizeDirection) =>
	direction === DragResizeDirection.LEFT ||
	direction === DragResizeDirection.RIGHT
		? Axis.X
		: Axis.Y;
const getDirectionAsNumber = (direction: DragResizeDirection) =>
	direction === DragResizeDirection.LEFT ||
	direction === DragResizeDirection.TOP
		? -1
		: 1;

export type UseDragResizeProps = {
	closed?: boolean;
	defaultSize?: number;
	direction?: DragResizeDirection;
	disabled?: boolean;
	maxSize?: number;
	minSize?: number;
	size?: number;
	/** TODO: changing this after the fact will result in unwanted results */
	overflowParentEl?: React.RefObject<HTMLElement>;
	userPref?: string;
	onResize?: (size: number) => void;
	onResizeStarted?: () => void;
	onResizeEnded?: (size: number) => void;
};

export const useDragResize = ({
	closed,
	defaultSize,
	disabled,
	maxSize,
	// there will always be a minimum size for the sake of math the end user
	minSize = 10,
	size,
	direction = DRAG_RESIZE_DEFAULT_DIRECTION,
	overflowParentEl,
	userPref,
	onResize,
	onResizeEnded,
	onResizeStarted,
}: UseDragResizeProps) => {
	const [userPrefSize, setUserPrefSize, unsetUserPrefSize] = useUserPref<
		false,
		number
	>(userPref, {
		parseIn: useCallback(
			(value?: string) => (value != null ? parseInt(value) : size || 0),
			[],
		),
	});

	const [currentSize, setCurrentSize] = useState(userPrefSize);
	const [isDragging, setIsDragging] = useState(false);
	const isDraggingRef = useRef(isDragging);

	const initialSizeRef = useRef(0);
	const lastSizeRef = useRef(0);
	const pointerStartRef = useRef(0);
	const lastMouseDownRef = useRef(Date.now());
	const limitSize = useRef(0);
	const draggedOnce = useRef(false);

	const maxSizeRef = useRef(maxSize);
	maxSizeRef.current = maxSize;
	const minSizeRef = useRef(minSize);
	minSizeRef.current = minSize;
	const currentSizeRef = useRef(currentSize);
	currentSizeRef.current = currentSize;

	const sizeDirection = useRef(getDirectionAsNumber(direction));
	useEffect(() => {
		sizeDirection.current = getDirectionAsNumber(direction);
	}, [direction]);

	const axis = getAxisFromDirection(direction);
	const axisRef = useRef(axis);
	axisRef.current = axis;

	const timeout = useRef<NodeJS.Timeout>();

	const [recheck, setRecheck] = useState({});

	const checkLimits = useCallback(() => {
		timeout.current && clearTimeout(timeout.current);

		requestAnimationFrame(() => {
			const overflowEl = overflowParentEl?.current;
			if (!overflowEl) return;

			const axis = axisRef.current;
			const clientSize = Math.round(overflowEl[ClientDimension[axis]]);
			const scrollSize = Math.round(overflowEl[ScrollDimension[axis]]);
			const minSize = minSizeRef.current;

			setCurrentSize((currentSize) => {
				if (clientSize < scrollSize || currentSize >= clientSize) {
					let newSize = currentSize - (scrollSize - clientSize);
					if (newSize < minSize) newSize = minSize;
					const limitToSize = newSize;
					if (draggedOnce.current) limitSize.current = limitToSize;
					timeout.current = setTimeout(() => setRecheck({}), 200);
					return newSize;
				}
				return currentSize;
			});
		});
	}, []);

	// Cleanup timeout(s)
	useEffect(() => {
		return () => {
			timeout.current && clearTimeout(timeout.current);
		};
	}, []);

	// When the overflow parent size changes
	// this can be from other elements on the page or the window itself resizing
	// we want to keep all resizable areas in bounds
	useEffect(() => {
		if (disabled) {
			setIsDragging(false);
			return;
		}

		if (!overflowParentEl?.current) return;
		const RO = new ResizeObserver(
			_.debounce(() => {
				setRecheck({});
			}, 16),
		);
		RO.observe(overflowParentEl.current);

		return () => {
			RO.disconnect();
		};
	}, [disabled]);

	useEffect(() => {
		if (!isDragging) return;
		// setting global style via jss is EXPENSIVE
		const classes = [`drag-resizing`, `drag-resizing-${axisRef.current}`];
		document.documentElement.classList.add(...classes);
		return () => {
			document.documentElement.classList.remove(...classes);
		};
	}, [isDragging]);

	useEffect(() => {
		if (lastSizeRef.current === currentSize) return;
		lastSizeRef.current = currentSize;
		onResize?.(currentSize);
	});

	useLayoutEffect(() => {
		if (isDraggingRef.current === isDragging) return;
		isDraggingRef.current = isDragging;
		if (isDragging) {
			onResizeStarted?.();
			return;
		}
		setUserPrefSize(currentSize);
		onResizeEnded?.(currentSize);
	});

	useLayoutEffect(() => {
		if (!isDragging) return;

		const mousemove = _.throttle(
			(event: MouseEvent): void => {
				draggedOnce.current = true;

				const axis = axisRef.current;
				const maxSize = limitSize.current || maxSizeRef.current;
				const minSize = minSizeRef.current;
				const initialSize = initialSizeRef.current;
				const startPosition = pointerStartRef.current;

				let size = Math.round(
					initialSize +
						(event[ClientPosition[axis]] - startPosition) *
							sizeDirection.current,
				);

				if (maxSize && maxSize < size) {
					size = maxSize;
				} else if (minSize && minSize > size) {
					size = minSize;
				}

				setCurrentSize((currentSize) => {
					if (size !== currentSize) setRecheck({});
					return size;
				});
			},
			16,
			{trailing: true},
		);

		const mouseup = () => {
			setIsDragging(false);
		};
		document.addEventListener('mousemove', mousemove);
		document.addEventListener('mouseup', mouseup);
		return () => {
			document.removeEventListener('mousemove', mousemove);
			document.removeEventListener('mouseup', mouseup);
		};
	}, [isDragging]);

	// When the width or overflow parent is changed
	useLayoutEffect(() => {
		checkLimits();
	}, [recheck, disabled]);

	const handleMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			if (disabled) return;

			event.stopPropagation();

			limitSize.current = 0;

			if (Date.now() - lastMouseDownRef.current < 300) {
				setCurrentSize(0);
				unsetUserPrefSize();
				onResizeEnded?.(0);
			} else {
				const axis = axisRef.current;
				initialSizeRef.current =
					(event.target as HTMLElement)?.parentElement?.[
						OffsetDimension[axis]
					] || 0;
				pointerStartRef.current = event[ClientPosition[axis]];
				setIsDragging(true);
				lastMouseDownRef.current = Date.now();
			}
		},
		[disabled],
	);

	const styles = useMemo(() => {
		if (disabled) return undefined;
		const size = closed ? 0 : currentSize || defaultSize;
		// if (!size) return undefined;
		return axis === Axis.X
			? {width: size, minWidth: size, maxWidth: size}
			: {height: size, minHeight: size, maxHeight: size};
	}, [axis, currentSize, disabled, defaultSize, closed]);

	return {
		direction,
		isDragging,
		styles,
		handleMouseDown,
		checkLimits,
	};
};

export default useDragResize;
